import {Layout, Card, Table, Image, Space, Button, Popconfirm, notification, Tag} from "antd";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteProduct} from "./api.js";
import {Suspense, useCallback, useMemo} from "react";
import ProductForm from "./ProductForm.jsx";
import {getProducts} from "../../../api/products.endpoint.js";
import {useAppSettings} from "../../../context/AppSettingsContext.jsx";
import ProductMediaManagement from "./ProductMediaManagement.jsx";

export const ProductManagement = ({jwt}) => {
    const queryClient = useQueryClient();
    const { getSettingByKey } = useAppSettings();
    const [api, contextHolder] = notification.useNotification();
    const {data = [], isLoading} = useQuery({queryKey: ['products', 'all'], queryFn: getProducts});

    const mutation = useMutation({
        mutationFn: (id) => deleteProduct(id, jwt)
    });

    const dataSource = useMemo(() => data.filter(({ln}) => ln === 'ru'), [data]);

    const staticUrl = getSettingByKey('STATIC_URL');
    const productTemplates = getSettingByKey('PRODUCTS_TEMPLATE');

    const findTemplateLabel = useCallback((template) => productTemplates.find(t => t.value === template)?.label, [productTemplates]);

    const onDeleteHandler = useCallback((productId) => async () => {
        try {
            await mutation.mutateAsync(productId);
            await queryClient.invalidateQueries({ queryKey: ['products', 'all'] });
            api.success({ message: 'Товар успешно удален!' });
        } catch (e) {
            api.error({ message: 'Ошибка при удалении товара!'})
            console.error(e)
        }
    }, [api, mutation, queryClient]);

    const columns = [
        {
            title: "Фото",
            dataIndex: "image",
            render: (img, product) => <Image
                width={50}
                alt={img}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9znv4yvJynLv4uLiV2dBoDiBf4qP3/ARuCRABEFAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghgg0Aj8i0JO4OzsrPv69Wv+hi2qPHr0qNvf39+iI97soRIh4f3z58/u7du3SXX7Xt7Z2enevHmzfQe+oSN2apSAPj09TSrb+XKI/f379+08+A0cNRE2ANkupk+ACNPvkSPcAAEibACyXUyfABGm3yNHuAECRNgAZLuYPgEirKlHu7u7XdyytGwHAd8jjNyng4OD7vnz51dbPT8/7z58+NB9+/bt6jU/TI+AGWHEnrx48eJ/EsSmHzx40L18+fLyzxF3ZVMjEyDCiEDjMYZZS5wiPXnyZFbJaxMhQIQRGzHvWR7XCyOCXsOmiDAi1HmPMMQjDpbpEiDCiL358eNHurW/5SnWdIBbXiDCiA38/Pnzrce2YyZ4//59F3ePLNMl4PbpiL2J0L979+7yDtHDhw8vtzzvdGnEXdvUigSIsCLAWavHp/+qM0BcXMd/q25n1vF57TYBp0a3mUzilePj4+7k5KSLb6gt6ydAhPUzXnoPR0dHl79WGTNCfBnn1uvSCJdegQhLI1vvCk+fPu2ePXt2tZOYEV6/fn31dz+shwAR1sP1cqvLntbEN9MxA9xcYjsxS1jWR4AIa2Ibzx0tc44fYX/16lV6NDFLXH+YL32jwiACRBiEbf5KcXoTIsQSpzXx4N28Ja4BQoK7rgXiydbHjx/P25TaQAJEGAguWy0+2Q8PD6/Ki4R8EVl+bzBOnZY95fq9rj9zAkTI2SxdidBHqG9+skdw43borCXO/ZcJdraPWdv22uIEiLA4q7nvvCug8WTqzQveOH26fodo7g6uFe/a17W3+nFBAkRYENRdb1vkkz1CH9cPsVy/jrhr27PqMYvENYNlHAIesRiBYwRy0V+8iXP8+/fvX11Mr7L7ECueb/r48eMqm7FuI2BGWDEG8cm+7G3NEOfmdcTQw4h9/55lhm7DekRYKQPZF2ArbXTAyu4kDYB2YxUzwg0gi/41ztHnfQG26HbGel/crVrm7tNY+/1btkOEAZ2M05r4FB7r9GbAIdxaZYrHdOsgJ/wCEQY0J74TmOKnbxxT9n3FgGGWWsVdowHtjt9Nnvf7yQM2aZU/TIAIAxrw6dOnAWtZZcoEnBpNuTuObWMEiLAx1HY0ZQJEmHJ3HNvGCBBhY6jtaMoEiJB0Z29vL6ls58vxPcO8/zfrdo5qvKO+d3Fx8Wu8zf1dW4p/cPzLly/dtv9Ts/EbcvGAHhHyfBIhZ6NSiIBTo0LNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiEC/wGgKKC4YMA4TAAAAABJRU5ErkJggg=="
                src={product.media ? product.image : `${staticUrl}/uploads/${img}`}
            />
        },
        {
            title: "Название",
            dataIndex: 'name'
        },
        {
            title: "Цена",
            dataIndex: "price",
            render: (price) => price || 0
        },
        {
            title: "Шаблон",
            dataIndex: "template",
            render: (template) => template === 'default' ? <Tag color="#2db7f5">По-умолчанию</Tag> : <Tag color="#87d068">{findTemplateLabel(template)}</Tag>
        },
        {
            title: null,
            dataIndex: "id",
            render: (id, product) => (
                <Space.Compact>
                    <ProductMediaManagement product={product} />
                    <Suspense>
                        <ProductForm productId={id} products={data} />
                    </Suspense>
                    <Popconfirm
                        title="Удалить товар?"
                        description="Это действие нельзя будет отменить"
                        onConfirm={onDeleteHandler(id)}
                        okText="Удалить"
                        cancelText="Отмена"
                    >
                        <Button danger type="primary" size="small" loading={mutation.isLoading}>
                            Удалить
                        </Button>
                    </Popconfirm>

                </Space.Compact>
            )
        }
    ];

    return (
        <Layout>
            {contextHolder}
            <Layout.Content>
                <Card title="Все товары">
                    <Table dataSource={dataSource} columns={columns} rowKey="_id" loading={isLoading}/>
                </Card>
            </Layout.Content>
        </Layout>
    )
};